import { Box, Button, Grid2 as Grid, Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useTranslation } from "react-i18next"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import RimediazioneForm from "../components/forms/RimediazioneForm"
import { useQsFilters } from "../hooks/filters"
import {
  useRimediazioneCreate,
  useRimediazioniList,
  useRimediazioneDelete,
  useRimediazioneUpdate,
} from "../hooks/rimediazioni"
import { Rimediazione } from "../types"
import RimediazioneCard from "../components/RimediazioneCard"
import DialogConfirm from "../components/DialogConfirm"
import { useSnackbarMessage } from "../hooks/snackbar"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useIsMobile } from "../hooks/mobile"
import { useSupporto } from "../hooks/supporti"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
})

type RimediazioniProps = {
  supportoId: number
}

export default function Rimediazioni({ supportoId }: RimediazioniProps) {
  const { t } = useTranslation()
  const { filters } = useQsFilters(initFilters)
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const { data: supporto } = useSupporto(supportoId)

  const { data } = useRimediazioniList({
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    supporto: supportoId.toString(),
  })

  const { mutateAsync: create } = useRimediazioneCreate()
  const { mutateAsync: update } = useRimediazioneUpdate()
  const { mutateAsync: remove } = useRimediazioneDelete()

  const [createModalState, createModalActions] = useModalTrigger()
  const [updateModalState, updateModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  return (
    <Layout>
      <Stack sx={{ minHeight: "100%" }}>
        <Box
          sx={{
            textAlign: "right",
            position: "sticky",
            top: 0,
            zIndex: 1,
            justifyContent: "flex-end",
            backgroundColor: "grey.100",
            borderBottom: 1,
            borderColor: "grey.300",
          }}
          px={3}
          py={1}
        >
          {userCanWriteAndDelete && !isMobile && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("rimediazione.cta_aggiungi")}
            </Button>
          )}
        </Box>

        <Box flexGrow={1} px={3} pb={3} pt={1}>
          <Grid container spacing={2}>
            {data?.results.map((rimediazione) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                  lg: 6,
                  xl: 4,
                }}
                key={rimediazione.id}
              >
                <RimediazioneCard
                  rimediazione={rimediazione}
                  onUpdate={() => {
                    updateModalActions.open(rimediazione)
                  }}
                  onDelete={() => {
                    deleteModalActions.open(rimediazione)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>

      <DialogForm<Rimediazione>
        open={createModalState.isOpen}
        hasCancel
        handleClose={createModalActions.close}
        title={t("rimediazione.cta_aggiungi")}
        subtitle={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Typography variant="body2" component="div">
              {supporto?.segnatura}<br/>
              <b>{t('supporto.framerate')}:</b> {supporto?.framerate_data?.termine || 'N/A'}&nbsp;&nbsp;
              <b>{t('supporto.sist_colore')}:</b> {supporto?.sist_colore_data?.termine || 'N/A'}&nbsp;&nbsp;
              <b>{t('supporto.cromatismo')}:</b> {supporto?.cromatismo_data?.termine || 'N/A'}&nbsp;&nbsp;
              <b>{t('supporto.audio')}:</b> {supporto?.audio_data?.termine || 'N/A'}&nbsp;&nbsp;
            </Typography>
          </Box>
        }
        FormComponent={RimediazioneForm}
        save={(values: Rimediazione) => {
          const newValues = {
            ...values,
            supporto: supportoId,
          }
          return create(newValues as Rimediazione).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Rimediazione>
        open={updateModalState.isOpen}
        hasCancel
        handleClose={updateModalActions.close}
        title={t("rimediazione.cta_modifica")}
        FormComponent={RimediazioneForm}
        item={updateModalState.value}
        save={(values: Rimediazione) => {
          return update(values).then(() => {
            updateModalActions.close()
            showMessage(t("rimediazione.esito_modifica_ok"))
          })
        }}
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("rimediazione.cta_elimina")}
        subtitle={
          <span>
            {t("rimediazione.cta_subtitle_elimina")} <b>"{deleteModalState.value?.data}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id).then(() => {
            deleteModalActions.close()
            showMessage(t("rimediazione.esito_elimina_ok"))
          }).catch(() => {
            deleteModalActions.close()
            showMessage(t("general.esito_elimina_errore"), "error")
          })
        }}
      />
    </Layout>
  )
}
